import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Puzzle, Cog, Rocket, Shield, Workflow, Laptop } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function ServicesAiServices() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Services · AI Services</p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            Build and run AI with clear scope and ownership
          </h1>
          <p className="mt-6 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            We help you define what to automate, what to pilot, and what to keep under human control—so initiatives land
            in production with support from security, risk, and the business.
          </p>
        </RfPageFrame>
      </div>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Overview Section */}
          <div className="space-y-6">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Service Overview</h2>
            <p className="text-[#5c5c5c] leading-relaxed max-w-4xl">
              RakFort's AI Services are geared towards practical, secure implementation. We don't just build models; 
              we build production-ready systems that integrate seamlessly with your existing infrastructure. 
              Our focus is on creating AI solutions that are not only innovative but also maintainable, 
              governable, and securely deployed.
            </p>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Categories / Pillars */}
          <div className="space-y-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Typical engagements</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Generative AI Roadmaps</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Strategic roadmaps for GenAI and copilots aligned to your risk appetite and business value drivers.
                </p>
              </div>

              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <Puzzle className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Integration Patterns</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Standardised integration patterns with existing data platforms, identity systems, and legacy APIs.
                </p>
              </div>

              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <Workflow className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Operating Models</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Defining roles, checkpoints, and handoffs between product, IT, security, and compliance teams.
                </p>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Why Us - Pillars */}
          <div className="space-y-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Why Choose RakFort?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <Shield className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Security-First</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  We bake security and governance into the development lifecycle from day one, not as an afterthought.
                </p>
              </div>
              <div className="space-y-3">
                <Cog className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Engineering Rigor</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  Our team applies software engineering best practices to AI, ensuring reliability and scalability.
                </p>
              </div>
              <div className="space-y-3">
                <Laptop className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Vendor Agnostic</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  We work across major cloud providers and model families to find the best fit for your specific needs.
                </p>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Our Approach */}
          <div className="rf-card p-8 md:p-10 bg-white border-dashed border-2">
            <h2 className="text-xl font-semibold text-[#2c2c2c] mb-6">Our Engagement Approach</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">01</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Opportunity Identification</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">We identify high-impact, low-risk use cases that align with your business strategy and technical readiness.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">02</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Prototype & Pilot</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Rapid development of a proof-of-concept to validate the technical feasibility and business value in a controlled environment.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">03</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Production Readiness</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Hardening the solution, implementing monitoring, and ensuring full compliance with security and data policies.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">04</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Operational Handoff</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Seamless transition to your internal teams with complete documentation, training, and support models.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-12 text-[0.9375rem] text-[#5c5c5c]">
            <Link to="/contactus" className="text-[#005a9c] font-medium hover:text-[#004a82] inline-flex items-center gap-1">
              Discuss a scope <ArrowRight className="w-4 h-4" />
            </Link>
          </p>
        </RfPageFrame>
      </section>
    </>
  );
}
